/* NVN Marine — grid page orchestrator (search results + collection PLP)
 *
 * Drives the ES-backed grid, facet rail, sort, and pagination on both:
 *   - /search                   (mode: "search",     seed config: search.q)
 *   - /collections/<handle>     (mode: "collection", seed config: collection handle/title)
 *
 * Wiring:
 *   <script type="application/json" id="nvn-grid-config">{ "mode":"...", ... }</script>
 *   <main data-nvn-grid-root>
 *     <aside data-nvn-facets></aside>
 *     <div   data-nvn-controls></div>     <!-- count, sort, view toggle -->
 *     <div   data-nvn-chips></div>         <!-- active filter chips -->
 *     <div   data-plp-grid></div>          <!-- existing class, ES card HTML injected -->
 *     <div   data-plp-list hidden></div>
 *     <div   data-nvn-pager></div>
 *     <div   data-nvn-fallback hidden></div> <!-- Liquid first-paint markup, shown on ES failure -->
 *   </main>
 */
(function () {
  'use strict';

  var NVN = (window.NVN = window.NVN || {});
  var root, cfg, facetsEl, controlsEl, chipsEl, gridEl, listEl, pagerEl, fallbackEl, countEl;
  var state = {
    q: '',
    page: 1,
    per_page: 24,
    sort: 'relevance',
    vendor: [],
    product_type: [],
    tags: [],
    shipping: [],
    price_min: '',
    price_max: '',
    in_stock: '',
    on_sale: ''
  };
  var collectionMap = null;
  var tagDict = null;
  var implicitFilter = {}; // applied silently for collection mode

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.from((ctx || document).querySelectorAll(sel)); }
  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /* -- URL state ---------------------------------------------------------- */
  // Array-valued, comma-joined in the query string. `shipping` rides this path
  // so a selection is sticky/shareable as ?shipping=Free Shipping,Fast %26 Free…
  // Spec facets (see SPEC_FACETS in nvn-facets.js); key = URL key = ES param.
  var SPEC_KEYS = ['model', 'voltage', 'thrust', 'shaft_length', 'screen_size', 'gph',
    'speaker_size', 'vhf_type', 'amps', 'banks', 'watts', 'btu'];
  SPEC_KEYS.forEach(function (k) { state[k] = []; });
  var ARR_KEYS = ['vendor', 'product_type', 'tags', 'shipping'].concat(SPEC_KEYS);
  function readURL() {
    var qs = new URLSearchParams(window.location.search);
    state.q = qs.get('q') || (cfg && cfg.q) || '';
    state.page = parseInt(qs.get('page') || '1', 10) || 1;
    state.sort = qs.get('sort') || 'relevance';
    state.price_min = qs.get('price_min') || '';
    state.price_max = qs.get('price_max') || '';
    state.in_stock = qs.get('in_stock') || '';
    state.on_sale = qs.get('on_sale') || '';
    ARR_KEYS.forEach(function (k) {
      var v = qs.get(k);
      state[k] = v ? v.split(',').filter(Boolean) : [];
    });
  }
  function writeURL(push) {
    var qs = new URLSearchParams();
    if (cfg.mode === 'search' && state.q) qs.set('q', state.q);
    if (state.page > 1) qs.set('page', String(state.page));
    if (state.sort && state.sort !== 'relevance') qs.set('sort', state.sort);
    if (state.price_min) qs.set('price_min', state.price_min);
    if (state.price_max) qs.set('price_max', state.price_max);
    if (state.in_stock) qs.set('in_stock', state.in_stock);
    if (state.on_sale) qs.set('on_sale', state.on_sale);
    ARR_KEYS.forEach(function (k) {
      if (state[k] && state[k].length) qs.set(k, state[k].join(','));
    });
    var url = window.location.pathname + (qs.toString() ? '?' + qs.toString() : '');
    if (push) window.history.pushState({ nvn: true }, '', url);
    else window.history.replaceState({ nvn: true }, '', url);
    applyFacetedRobots();
  }

  /* -- Build the params we send to ES ------------------------------------- */
  function buildParams() {
    var p = {
      q: state.q || '',
      page: state.page,
      per_page: state.per_page,
      sort: state.sort,
      price_min: state.price_min,
      price_max: state.price_max,
      in_stock: state.in_stock,
      on_sale: state.on_sale,
      vendor: (state.vendor || []).slice(),
      product_type: (state.product_type || []).slice(),
      tags: (state.tags || []).slice(),
      // URL/state key is `shipping`; the wrapper filter param is `shipping_type`
      // (the ES field). Comma-joined + OR semantics handled by the client.
      shipping_type: (state.shipping || []).slice()
    };
    SPEC_KEYS.forEach(function (k) { p[k] = (state[k] || []).slice(); });
    // Merge implicit filter (collection scope)
    Object.keys(implicitFilter).forEach(function (k) {
      if (Array.isArray(p[k])) {
        if (Array.isArray(implicitFilter[k])) {
          implicitFilter[k].forEach(function (v) { if (p[k].indexOf(v) === -1) p[k].push(v); });
        } else if (p[k].indexOf(implicitFilter[k]) === -1) {
          p[k].push(implicitFilter[k]);
        }
      } else if (!p[k]) {
        p[k] = implicitFilter[k];
      }
    });
    return p;
  }

  /* -- Rendering ---------------------------------------------------------- */
  function listViewActive() {
    try { return localStorage.getItem('nvn_plp_view') === 'list'; } catch (_) { return false; }
  }

  function renderGrid(products) {
    if (!products || !products.length) {
      var msg;
      if (cfg.mode === 'search') {
        msg = '<div class="plp-empty">' +
          '<h2>No results for &ldquo;' + esc(state.q) + '&rdquo;</h2>' +
          '<p>Try a different spelling, a broader term, or browse our catalog.</p>' +
          '<p style="margin-top:14px"><a class="btn btn-primary" href="/collections/all">Browse all products</a> ' +
          '<a class="btn btn-ghost" href="#" data-facets-clear>Clear filters</a></p>' +
          '</div>';
      } else {
        msg = '<div class="plp-empty">' +
          '<h2>No products match the selected filters.</h2>' +
          '<p><a href="#" data-facets-clear>Clear filters</a> to see all products in this category.</p>' +
          '</div>';
      }
      gridEl.innerHTML = msg;
      if (listEl) listEl.innerHTML = '';
      return;
    }
    // Apply "in-stock first" client sort if user hasn't picked a sort
    // ES is responsible for the ordering (in-stock first is handled
    // server-side). A client-side sort here would only reorder the visible
    // page, which is misleading across pagination — keep ES's order.
    var sorted = products;
    var gridHTML = '', listHTML = '';
    sorted.forEach(function (p, i) {
      gridHTML += NVN.cards.renderGridCard(p, i);
      listHTML += NVN.cards.renderListCard(p, i);
    });
    gridEl.innerHTML = gridHTML;
    if (listEl) listEl.innerHTML = listHTML;
    fireViewItemList(sorted, lastMeta);
  }
  var lastMeta = null;

  function fireViewItemList(products, meta) {
    try {
      var listName = cfg.listName || (cfg.mode === 'search' ? ('Search: ' + state.q) : '');
      var listId = cfg.listId || (cfg.mode === 'search' ? 'search' : '');
      var pageType = cfg.mode === 'search' ? 'Search' : 'Collection';
      var items = products.slice(0, 40).map(function (p, i) {
        var v = (p.variants && p.variants[0]) || {};
        var price = (p.price && p.price.min) || 0;
        var compareMin = (p.price && p.price.compare_at_min) || 0;
        var discount = compareMin > price ? (compareMin - price).toFixed(2) : null;
        return {
          item_id: 'shopify_us_' + p.id + '_' + v.id,
          item_name: p.title || '',
          item_brand: p.vendor || null,
          item_category: p.product_type || null,
          item_variant: v.title && v.title !== 'Default Title' ? v.title : null,
          item_list_name: listName,
          item_list_id: listId,
          price: price.toFixed(2),
          discount: discount,
          index: i,
          affiliation: 'NVN Marine',
          imageURL: p.featured_image || null,
          productURL: p.url || (p.handle ? '/products/' + p.handle : null),
          sku: v.sku || null
        };
      });
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        productList: listName,
        pageType: pageType,
        event: 'view_item_list',
        ecommerce: {
          item_list_name: listName,
          item_list_id: listId,
          items: items
        }
      });
      if (cfg.mode === 'search' && state.q) {
        try {
          document.dispatchEvent(new CustomEvent('nvn:search-rendered', {
            detail: { query: state.q, total: (meta && meta.total) || items.length, provider: 'elasticsearch' }
          }));
        } catch (_) {}
      }
    } catch (_) {}
  }

  function renderControls(total) {
    if (!controlsEl) return;
    var html = '<span class="plp-bar-count">' + (total || 0) + (total === 1 ? ' product' : ' products') + '</span>';
    html += '<div class="plp-bar-right">';
    html += '<button type="button" class="plp-filter-btn" data-filter-open aria-label="Open filters">';
    html += '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></svg>';
    html += '<span>Filters</span></button>';
    var sortOpts = [
      { value: 'relevance', label: 'Relevance' },
      { value: 'price_asc', label: 'Price ↑' },
      { value: 'price_desc', label: 'Price ↓' },
      { value: 'newest', label: 'Newest' }
    ];
    html += '<form class="plp-sort-form" data-nvn-sort-form>';
    html += '<label class="plp-sort-label" for="NvnSort">Sort</label>';
    html += '<div class="plp-sort-wrap">';
    html += '<select id="NvnSort" name="sort" class="plp-sort-select">';
    sortOpts.forEach(function (o) {
      html += '<option value="' + o.value + '"' + (o.value === state.sort ? ' selected' : '') + '>' + esc(o.label) + '</option>';
    });
    html += '</select>';
    html += '<svg class="plp-sort-caret" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>';
    html += '</div></form>';
    html += '<div class="plp-view" role="group" aria-label="View as">';
    html += '<button type="button" class="plp-view-btn" data-view-toggle="grid" aria-pressed="' + (!listViewActive()) + '" aria-label="Grid view" title="Grid view">';
    html += '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>';
    html += '</button>';
    html += '<button type="button" class="plp-view-btn" data-view-toggle="list" aria-pressed="' + (listViewActive()) + '" aria-label="List view" title="List view">';
    html += '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>';
    html += '</button>';
    html += '</div></div>';
    controlsEl.innerHTML = html;
  }

  function renderChips() {
    if (!chipsEl) return;
    var chips = [];
    function chip(label, kind, value) {
      chips.push('<a href="#" class="filter-chip" data-chip-remove data-chip-kind="' + esc(kind) + '" data-chip-value="' + esc(value || '') + '">' +
        esc(label) +
        '<svg class="x" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
        '</a>');
    }
    state.vendor.forEach(function (v) { chip(v, 'vendor', v); });
    state.product_type.forEach(function (v) {
      var parts = v.split('|').map(function (s) { return s.trim(); });
      chip(parts[parts.length - 1], 'product_type', v);
    });
    state.tags.forEach(function (v) {
      var entry = (tagDict && tagDict.tags && tagDict.tags[v]) || {};
      chip(entry.label || v, 'tags', v);
    });
    // Shipping chips — ES value verbatim (no relabeling).
    state.shipping.forEach(function (v) { chip(v, 'shipping', v); });
    NVN.facets.SPEC_FACETS.forEach(function (spec) {
      state[spec.key].forEach(function (v) {
        chip(NVN.facets.specLabel(spec, v), spec.key, v);
      });
    });
    if (state.price_min || state.price_max) {
      chip('Price ' + (state.price_min ? '$' + state.price_min : '$0') + '–' + (state.price_max ? '$' + state.price_max : '∞'), 'price', '');
    }
    if (state.in_stock) chip('In stock', 'in_stock', '');
    if (state.on_sale) chip('On sale', 'on_sale', '');
    if (chips.length) chips.push('<a href="#" class="filter-chips-clear" data-facets-clear>Clear all</a>');
    chipsEl.innerHTML = chips.join('');
  }

  function renderPager(meta) {
    if (!pagerEl) return;
    if (!meta || meta.total_pages <= 1) { pagerEl.innerHTML = ''; return; }
    var current = meta.page;
    var total = meta.total_pages;
    var parts = [];
    function btn(label, page, isActive, isDisabled) {
      if (isDisabled) return '<span class="pager-btn" style="opacity:.3">' + label + '</span>';
      if (isActive) return '<span class="pager-btn active">' + label + '</span>';
      return '<a href="#" class="pager-btn" data-nvn-page="' + page + '">' + label + '</a>';
    }
    var prevSvg = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>';
    var nextSvg = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>';
    parts.push(btn(prevSvg, current - 1, false, !meta.has_prev));

    // Smart page list: 1, …, current-1, current, current+1, …, last
    var pages = new Set([1, total, current, current - 1, current + 1, current - 2, current + 2]);
    var sorted = Array.from(pages).filter(function (n) { return n >= 1 && n <= total; }).sort(function (a, b) { return a - b; });
    var prev = 0;
    sorted.forEach(function (n) {
      if (n - prev > 1) parts.push('<span class="pager-btn" style="border:none;background:transparent">…</span>');
      parts.push(btn(String(n), n, n === current, false));
      prev = n;
    });
    parts.push(btn(nextSvg, current + 1, false, !meta.has_next));
    pagerEl.innerHTML = '<div class="pager">' + parts.join('') + '</div>';
  }

  function applyView(mode) {
    if (!gridEl || !listEl) return;
    if (mode === 'list') {
      gridEl.setAttribute('hidden', '');
      listEl.removeAttribute('hidden');
    } else {
      listEl.setAttribute('hidden', '');
      gridEl.removeAttribute('hidden');
    }
    $$('[data-view-toggle]', controlsEl).forEach(function (b) {
      b.setAttribute('aria-pressed', b.dataset.viewToggle === mode ? 'true' : 'false');
    });
  }

  /* -- Fetch + render cycle ---------------------------------------------- */
  var loading = false;
  async function fetchAndRender() {
    if (loading) return;
    loading = true;
    root.classList.add('nvn-loading');
    try {
      var res = await NVN.search.search(buildParams());
      if (!res.ok) {
        if (res.aborted) return;
        showFallback();
        return;
      }
      var json = res.json;
      if (!json.success || !json.data) { showFallback(); return; }
      lastMeta = json.meta || null;

      // ES facets are disjunctive: a facet's own selection doesn't narrow its
      // option list, so render meta.facets as-is.
      if (facetsEl) facetsEl.innerHTML = NVN.facets.render((json.meta && json.meta.facets) || {}, state, {
        suppress: cfg.suppressFacets || [],
        total: json.meta && json.meta.total
      });
      renderControls(json.meta && json.meta.total);
      renderChips();
      renderGrid(json.data);
      renderPager(json.meta);
      applyView(listViewActive() ? 'list' : 'grid');
    } finally {
      loading = false;
      root.classList.remove('nvn-loading');
    }
  }

  // On search page (no Liquid first paint) show inline error.
  // On collection page, silently keep the Liquid grid visible.
  function showFallback() {
    if (cfg.mode === 'search' && gridEl) {
      gridEl.innerHTML = '<div class="plp-empty"><p>Search is temporarily unavailable. Please try again.</p></div>';
      if (facetsEl) facetsEl.innerHTML = '';
    }
  }

  /* -- Event handlers ---------------------------------------------------- */
  function readFacetsToState() {
    var form = $('[data-facet-form]', facetsEl);
    if (!form) return;
    var s = NVN.facets.readForm(form);
    state.vendor = Array.isArray(s.vendor) ? s.vendor : (s.vendor ? [s.vendor] : []);
    state.product_type = Array.isArray(s.product_type) ? s.product_type : (s.product_type ? [s.product_type] : []);
    state.tags = Array.isArray(s.tags) ? s.tags : (s.tags ? [s.tags] : []);
    state.shipping = Array.isArray(s.shipping) ? s.shipping : (s.shipping ? [s.shipping] : []);
    SPEC_KEYS.forEach(function (k) { state[k] = s[k] || []; });
    state.price_min = s.price_min || '';
    state.price_max = s.price_max || '';
    state.in_stock = s.in_stock || '';
    state.on_sale = s.on_sale || '';
  }

  function clearAll() {
    state.vendor = []; state.product_type = []; state.tags = []; state.shipping = [];
    SPEC_KEYS.forEach(function (k) { state[k] = []; });
    state.price_min = ''; state.price_max = '';
    state.in_stock = ''; state.on_sale = '';
    state.page = 1;
  }

  function bind() {
    // Facet input changes (debounced)
    var changeTimer;
    root.addEventListener('change', function (e) {
      if (e.target.closest('[data-facet-input]')) {
        clearTimeout(changeTimer);
        changeTimer = setTimeout(function () {
          readFacetsToState();
          state.page = 1;
          writeURL(true);
          fetchAndRender();
        }, 150);
      } else if (e.target.matches('[name="sort"]') && e.target.closest('[data-nvn-sort-form]')) {
        state.sort = e.target.value;
        state.page = 1;
        writeURL(true);
        fetchAndRender();
      }
    });

    // Click handlers (chips, pager, clear, view toggle, facet show-more, filter open/close)
    root.addEventListener('click', function (e) {
      // Open the facet rail as a slide-in drawer on mobile
      if (e.target.closest('[data-filter-open]')) {
        e.preventDefault();
        if (facetsEl) {
          facetsEl.classList.add('is-open');
          document.body.classList.add('nvn-filter-open');
          // Inject a tappable scrim sibling so users can dismiss with a tap
          // outside the rail (CSS ::before can't receive click events cleanly).
          if (!document.querySelector('[data-nvn-filter-scrim]')) {
            var scrim = document.createElement('div');
            scrim.setAttribute('data-nvn-filter-scrim', '');
            scrim.style.cssText = 'position:fixed;inset:0;background:rgba(7,16,28,0.5);z-index:9999;';
            // Scrim lives on document.body, outside `root`, so root's
            // delegated click handler never sees these clicks. Bind here.
            scrim.addEventListener('click', function () {
              if (facetsEl) facetsEl.classList.remove('is-open');
              document.body.classList.remove('nvn-filter-open');
              scrim.remove();
            });
            document.body.appendChild(scrim);
          }
        }
        return;
      }
      // Close it: close button inside rail or tap on scrim
      if (e.target.closest('[data-filter-close]') || e.target.closest('[data-nvn-filter-scrim]')) {
        e.preventDefault();
        if (facetsEl) facetsEl.classList.remove('is-open');
        document.body.classList.remove('nvn-filter-open');
        var existingScrim = document.querySelector('[data-nvn-filter-scrim]');
        if (existingScrim) existingScrim.remove();
        return;
      }
      var showMore = e.target.closest('[data-facet-show-more]');
      if (showMore) {
        e.preventDefault();
        var group = showMore.previousElementSibling;
        if (group && group.hasAttribute('data-facet-overflow')) {
          var collapsed = group.hasAttribute('hidden');
          if (collapsed) group.removeAttribute('hidden'); else group.setAttribute('hidden', '');
          showMore.querySelector('.more').toggleAttribute('hidden', collapsed);
          showMore.querySelector('.less').toggleAttribute('hidden', !collapsed);
        }
        return;
      }
      var chip = e.target.closest('[data-chip-remove]');
      if (chip) {
        e.preventDefault();
        var kind = chip.dataset.chipKind;
        var value = chip.dataset.chipValue;
        if (kind === 'vendor') state.vendor = state.vendor.filter(function (v) { return v !== value; });
        else if (kind === 'product_type') state.product_type = state.product_type.filter(function (v) { return v !== value; });
        else if (kind === 'tags') state.tags = state.tags.filter(function (v) { return v !== value; });
        else if (kind === 'shipping') state.shipping = state.shipping.filter(function (v) { return v !== value; });
        else if (SPEC_KEYS.indexOf(kind) !== -1) state[kind] = state[kind].filter(function (v) { return v !== value; });
        else if (kind === 'price') { state.price_min = ''; state.price_max = ''; }
        else if (kind === 'in_stock') state.in_stock = '';
        else if (kind === 'on_sale') state.on_sale = '';
        state.page = 1;
        writeURL(true);
        fetchAndRender();
        return;
      }
      var clear = e.target.closest('[data-facets-clear]');
      if (clear) {
        e.preventDefault();
        clearAll();
        writeURL(true);
        fetchAndRender();
        return;
      }
      var page = e.target.closest('[data-nvn-page]');
      if (page) {
        e.preventDefault();
        var n = parseInt(page.dataset.nvnPage, 10);
        if (n >= 1) {
          state.page = n;
          writeURL(true);
          fetchAndRender();
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        return;
      }
      var view = e.target.closest('[data-view-toggle]');
      if (view) {
        var mode = view.dataset.viewToggle;
        applyView(mode);
        try { localStorage.setItem('nvn_plp_view', mode); } catch (_) {}
        return;
      }
    });

    window.addEventListener('popstate', function () {
      readURL();
      fetchAndRender();
    });
  }

  /* -- Boot --------------------------------------------------------------- */
  async function loadDicts() {
    var ns = window.NVN_SEARCH || {};
    try {
      var [tagsRes, mapRes] = await Promise.all([
        ns.tagLabelsUrl ? fetch(ns.tagLabelsUrl) : Promise.resolve({ ok: false }),
        ns.collectionMapUrl ? fetch(ns.collectionMapUrl) : Promise.resolve({ ok: false })
      ]);
      if (tagsRes.ok) tagDict = await tagsRes.json();
      if (mapRes.ok) collectionMap = await mapRes.json();
    } catch (_) {}
    if (NVN.facets && NVN.facets.setTagDict) NVN.facets.setTagDict(tagDict);
  }

  // Cache of all ES product_types + top-level tags so the auto-matcher can
  // resolve any new collection handle in one pass per session.
  var esTaxonomy = null;
  async function loadTaxonomy() {
    if (esTaxonomy) return esTaxonomy;
    var res = await NVN.search.search({ q: '', per_page: 1 });
    if (!res.ok) { esTaxonomy = { product_types: [], tags: [], vendors: [] }; return esTaxonomy; }
    var f = (res.json.meta && res.json.meta.facets) || {};
    esTaxonomy = {
      product_types: (f.product_types || []).map(function (p) { return p.value; }),
      tags: (f.tags || []).map(function (t) { return t.value; }),
      vendors: (f.vendors || []).map(function (v) { return v.value; })
    };
    return esTaxonomy;
  }

  // Match handle tokens against known ES vendor names. Returns the matched
  // vendor value (e.g. "Raymarine") or null. Used for brand-model collections
  // like /collections/raymarine-axiom-plus where the products are tagged by
  // technical category, not brand.
  function detectVendorInHandle(handle, vendors) {
    if (!handle || !vendors || !vendors.length) return null;
    var hTok = tokens(handle);
    if (!hTok.length) return null;
    var hit = null;
    vendors.forEach(function (v) {
      if (hit) return;
      var vTok = tokens(v);
      if (!vTok.length) return;
      // Vendor name fully contained in the handle (every vendor token is in
      // the handle). Avoids partial matches like "Air" matching "Airmar".
      var allFound = vTok.every(function (vt) { return hTok.indexOf(vt) !== -1; });
      if (allFound) hit = v;
    });
    return hit;
  }

  // Tokenize either a handle (dash-delimited) or an ES taxonomy string
  // ("Marine Navigation & Instruments | GPS - Chartplotters") into lowercase
  // tokens, dropping noise words. Plural/singular collapsed.
  var NOISE = { and: 1, the: 1, of: 1, with: 1, in: 1, on: 1, '&': 1 };
  function tokens(s) {
    return String(s || '').toLowerCase()
      .split(/[^a-z0-9]+/)
      .map(function (t) { return t.replace(/s$/, ''); }) // crude singularize
      .filter(function (t) { return t.length > 1 && !NOISE[t]; });
  }

  function matchTaxonomy(handle, candidates) {
    var hTok = tokens(handle);
    if (!hTok.length || !candidates.length) return null;
    var best = null, bestScore = 0;
    candidates.forEach(function (c) {
      var cTok = tokens(c);
      if (!cTok.length) return;
      var matches = 0;
      hTok.forEach(function (h) { if (cTok.indexOf(h) !== -1) matches++; });
      if (!matches) return;
      // Score: matches + handle coverage + candidate specificity bonus
      var coverage = matches / hTok.length;
      var specificity = matches / cTok.length;
      var score = matches + coverage + specificity * 0.5;
      if (score > bestScore) { bestScore = score; best = c; }
    });
    // Require at least 2 token matches OR the entire handle covered
    if (bestScore >= 2 || (best && tokens(best).length >= hTok.length && bestScore > 1)) return best;
    return null;
  }

  // Normalize a metafield value that may arrive as a Liquid array (already
  // a JSON array in the embedded config), a single string, or a delimited
  // string. Returns a string array, empty if blank.
  function normalizeMfList(v) {
    if (v == null || v === '') return [];
    if (Array.isArray(v)) return v.filter(function (x) { return x != null && String(x).trim(); }).map(function (x) { return String(x).trim(); });
    var s = String(v).trim();
    if (!s) return [];
    // Single value — keep as a 1-element list. We don't split on commas
    // because product_type values legitimately contain "Brand, Model".
    return [s];
  }

  async function resolveImplicit() {
    if (cfg.mode !== 'collection' || !cfg.handle) return;

    // 1) Authoritative — backend-curated collection metafields under
    //    collection.metafields.nvn.filter_*. When any of these are set,
    //    they ARE the collection scope. Shopper facet selections layer on
    //    top via mergeIntoParams (additive narrowing) but cannot escape
    //    this filter. Highest priority — pre-empts every other signal.
    if (cfg.hasNvnFilter) {
      var mfVendor = normalizeMfList(cfg.nvnFilterVendor);
      var mfType   = normalizeMfList(cfg.nvnFilterProductType);
      var mfTags   = normalizeMfList(cfg.nvnFilterTags);
      if (mfVendor.length) implicitFilter.vendor = mfVendor;
      if (mfType.length)   implicitFilter.product_type = mfType;
      if (mfTags.length)   implicitFilter.tags = mfTags;
      return;
    }

    // 2) Explicit override from nvn-collection-map.json — full control,
    //    no vendor auto-detect on top. Kept as a code-side escape hatch
    //    for collections without admin metafields wired yet.
    if (collectionMap && collectionMap[cfg.handle]) {
      var spec = collectionMap[cfg.handle];
      Object.keys(spec).forEach(function (k) {
        if (k.charAt(0) === '_') return;
        implicitFilter[k] = spec[k];
      });
      return;
    }

    // Vendor auto-detect from the handle. Brand-collections like
    // "raymarine-products-instruments" or "garmin-fishfinders" need the
    // vendor filter applied even when product_type/tags resolve cleanly,
    // otherwise the type-only filter lets sibling brands leak in. We do
    // this BEFORE step 3 so the vendor filter is always additive.
    var taxForBrand = await loadTaxonomy();
    var brandHit = detectVendorInHandle(cfg.handle, taxForBrand.vendors);
    if (brandHit) implicitFilter.vendor = brandHit;

    // 2) Trust Shopify-collected product_types from Liquid first paint.
    //    Authoritative: it's exactly the types Shopify considers part of
    //    this collection.
    var cleanTypes = (cfg.productTypes || []).filter(function (t) { return t && t.trim(); });
    if (cleanTypes.length) {
      implicitFilter.product_type = cleanTypes;
      return;
    }

    // 3) Liquid product_type was missing/empty — fall back to category-style
    //    tags Shopify exposes ("Communication | VHF - Fixed Mount" etc.).
    //    When Liquid scans the first 25 products it sometimes picks up an
    //    outlier tag from a mis-categorised product, which would OR-filter
    //    the entire catalog into the result set. Pick only the tag(s) that
    //    best match the collection handle's tokens.
    var cleanTags = (cfg.categoryTags || []).filter(function (t) { return t && t.trim(); });
    if (cleanTags.length === 1) {
      implicitFilter.tags = cleanTags;
      return;
    }
    if (cleanTags.length > 1) {
      var hTok = tokens(cfg.handle);
      var scored = cleanTags.map(function (t) {
        var cTok = tokens(t);
        var matches = 0;
        hTok.forEach(function (h) { if (cTok.indexOf(h) !== -1) matches++; });
        return { tag: t, matches: matches };
      });
      var topScore = Math.max.apply(null, scored.map(function (s) { return s.matches; }));
      // Keep only tags that share at least the same number of tokens as the
      // best match. This both filters outliers and preserves true multi-tag
      // collections (where several tags legitimately tie on score).
      var winners = scored.filter(function (s) { return s.matches > 0 && s.matches === topScore; })
                          .map(function (s) { return s.tag; });
      if (winners.length) {
        implicitFilter.tags = winners;
        return;
      }
      // No tag-token overlap at all — likely a brand-model collection like
      // "raymarine-axiom-plus" whose products are tagged by technical
      // category. Try to detect the brand in the handle and use it as a
      // vendor filter combined with the union of all categoryTags.
      var taxBrand = await loadTaxonomy();
      var brand = detectVendorInHandle(cfg.handle, taxBrand.vendors);
      if (brand) {
        implicitFilter.vendor = brand;
        implicitFilter.tags = cleanTags;
        return;
      }
      // Still no signal — use all tags as OR so we at least narrow to the
      // technical categories Shopify says are in this collection.
      implicitFilter.tags = cleanTags;
      return;
    }

    // 4) Auto-match handle tokens against ES taxonomy (last resort when
    //    Liquid had no products to sniff — e.g. empty rule-based collection).
    var tax = await loadTaxonomy();
    var typeHit = matchTaxonomy(cfg.handle, tax.product_types);
    if (typeHit) { implicitFilter.product_type = typeHit; return; }
    var tagHit = matchTaxonomy(cfg.handle, tax.tags);
    if (tagHit) { implicitFilter.tags = tagHit; return; }
  }

  // Flip the robots meta tag to noindex,follow when the user is on a faceted
  // collection URL (?vendor=, ?product_type=, ?tags=, etc.). Shopify Liquid
  // can't reliably read the query string server-side, so we mutate the meta
  // tag client-side. Googlebot executes JS and honours post-load mutations.
  function applyFacetedRobots() {
    if (cfg.mode !== 'collection') return;
    if (!window.location.search || window.location.search.length < 2) return;
    var meta = document.querySelector('meta[name="robots"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'robots';
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', 'noindex, follow');
  }

  async function boot() {
    var cfgScript = document.getElementById('nvn-grid-config');
    if (!cfgScript) return;
    try { cfg = JSON.parse(cfgScript.textContent || '{}'); } catch (_) { cfg = {}; }
    if (!cfg.mode) return;
    applyFacetedRobots();

    root = $('[data-nvn-grid-root]');
    if (!root) return;
    facetsEl = $('[data-nvn-facets]', root);
    controlsEl = $('[data-nvn-controls]', root);
    chipsEl = $('[data-nvn-chips]', root);
    gridEl = $('[data-plp-grid]', root);
    listEl = $('[data-plp-list]', root);
    pagerEl = $('[data-nvn-pager]', root);
    fallbackEl = $('[data-nvn-fallback]', root);
    countEl = $('[data-nvn-count]', root);

    state.per_page = parseInt(cfg.perPage || (NVN.config && NVN.config.perPage) || 24, 10);

    readURL();
    await loadDicts();
    await resolveImplicit();

    // Legacy-collection bail-out. When this is a collection page and the
    // resolver produced no implicit filter, we have no way to ask ES the
    // right question (rule-based collections still use internal X_* tags
    // the ES API doesn't index). Leave the Liquid first-paint visible and
    // skip ES init so we don't replace correct Shopify-rendered products
    // with a broader, wrong ES result set.
    var hasImplicit = Object.keys(implicitFilter).some(function (k) {
      var v = implicitFilter[k];
      return Array.isArray(v) ? v.length > 0 : v != null && v !== '';
    });
    if (cfg.mode === 'collection' && !hasImplicit) return;

    // Suppress facets whose value is implicit (already filtered by the
    // collection scope). The facet renderer will skip these groups entirely.
    cfg.suppressFacets = cfg.suppressFacets || [];
    if (implicitFilter.vendor) cfg.suppressFacets.push('vendor');
    if (implicitFilter.product_type) cfg.suppressFacets.push('product_type');

    bind();
    fetchAndRender();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
